import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';

interface DeleteRunDialogProps {
  open: boolean;
  runId: string;
  onClose: () => void;
  onCompleted?: () => void;
}

export function DeleteRunDialog({ open, runId, onClose, onCompleted }: DeleteRunDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runTitle, setRunTitle] = useState('');

  useEffect(() => {
    if (!open || !runId) return;
    setError(null);
    setRunTitle('');
    void apiClient.getAiWorkflowRun(runId)
      .then((data) => {
        const title = String(data?.run?.displayTitle || data?.run?.runId || runId);
        setRunTitle(title);
      })
      .catch(() => {
        setRunTitle(runId);
      });
  }, [open, runId]);

  const handleDelete = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.deleteAiWorkflowRun(runId);
      onCompleted?.();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent maxWidth="lg">
        <DialogHeader>
          <DialogTitle>Delete Run</DialogTitle>
          <DialogDescription>Permanent removal from AI Workflows history.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          )}
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Are you sure you want to delete run <span className="font-medium">{runTitle || runId}</span>? This action cannot be undone.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={submitting}>
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting...
              </span>
            ) : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
